import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { createRouter, createWebHashHistory } from 'vue-router'
import { createDiscreteApi } from 'naive-ui'
import * as Sentry from '@sentry/vue'
import axios from 'axios'
// 退避先 page（Login / Reconnecting）は遅延 import にしない。
// 退避先自体の chunk 取得に失敗すると復旧手段が無くなるため。
import LoginView from '@/views/LoginView.vue'
import ReconnectingView from '@/views/ReconnectingView.vue'
import { parseCustomerActionTab } from '@/types/customer.type'
import { parseSalesProfitQuery } from '@/types/order-aggregate.type'
import { parseSalesrepActivityQuery } from '@/types/salesrep-activity.type'
import { parseUsersQuery } from '@/types/user.type'
import type { MediaCut, ResultCut } from '@/types/order-ticket-metrics.type'
import type { AiInvolvement } from '@/types/order-ticket.type'
const { message } = createDiscreteApi(['message'])

export type RouteAccess = 'allBranches' | 'systemAdmin'

declare module 'vue-router' {
  interface RouteMeta {
    adminPage?: boolean // 管理画面（担当者として操作する利用者向け画面ではない）
    access?: RouteAccess // 開ける人の条件。指定が無ければログイン済みの全員が開ける（データはサーバが支店で絞る）
  }
}

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '',
      redirect: '/login',
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      props: (route: { query: { authError?: string } }) => ({
        authError: route.query.authError === 'true',
      }),
      meta: { requiredAuth: false },
    },
    {
      path: '/reconnecting',
      name: 'reconnecting',
      component: ReconnectingView,
      props: (route: { query: { redirect?: string } }) => ({
        redirect: route.query.redirect,
      }),
      meta: { requiredAuth: false },
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('@/views/ChatbotView.vue'),
      props: (route: { query: { cId?: string; new?: string } }) => ({
        conversationId: route.query.cId,
        isNewConversation: route.query.new === 'true',
      }),
      meta: { requiredAuth: true },
    },
    {
      path: '/customer-notes',
      name: 'customerNotes',
      component: () => import('@/views/CustomerNotesView.vue'),
      meta: { requiredAuth: true },
    },
    {
      path: '/market-intelligence',
      name: 'marketIntelligence',
      component: () => import('@/views/MarketIntelligenceView.vue'),
      meta: { requiredAuth: true },
    },
    {
      path: '/order-tickets',
      name: 'orderTickets',
      component: () => import('@/views/OrderTicketListView.vue'),
      props: (route: { query: { id?: string } }) => ({ orderTicketId: route.query.id }),
      meta: { requiredAuth: true },
    },
    {
      path: '/received-orders',
      name: 'receivedOrders',
      component: () => import('@/views/ReceivedOrderListView.vue'),
      meta: { requiredAuth: true },
    },
    {
      path: '/received-orders/:accountNumber/:orderHistoryId',
      name: 'receivedOrderDetail',
      component: () => import('@/views/ReceivedOrderDetailView.vue'),
      props: true,
      meta: { requiredAuth: true },
    },
    {
      path: '/deal-requests',
      name: 'dealRequests',
      component: () => import('@/views/DealRequestListView.vue'),
      meta: { requiredAuth: true },
    },
    {
      path: '/deal-requests/:id',
      name: 'dealRequestDetail',
      component: () => import('@/views/DealRequestDetailView.vue'),
      props: true,
      meta: { requiredAuth: true },
    },
    {
      path: '/admin/conversation-list',
      name: 'conversationList',
      component: () => import('@/views/admin/ConversationListView.vue'),
      props: (route: {
        query: { page?: string; agent?: string; currentId?: string; userId?: string }
      }) => ({
        page: route.query.page,
        agent: route.query.agent,
        currentId: route.query.currentId,
        userId: route.query.userId,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/speech-log-list',
      name: 'speechLogList',
      component: () => import('@/views/admin/SpeechLogListView.vue'),
      props: (route: { query: { page?: string; currentId?: string; userId?: string } }) => ({
        page: route.query.page,
        currentId: route.query.currentId,
        userId: route.query.userId,
      }),
      meta: { requiredAuth: true, adminPage: true, access: 'allBranches' },
    },
    {
      path: '/admin/order-ticket-list',
      name: 'orderTicketList',
      component: () => import('@/views/admin/OrderTicketListView.vue'),
      props: (route: {
        query: {
          userId?: string
          currentId?: string
          customerAccountNumber?: string
          monthFrom?: string
          monthTo?: string
          resultType?: ResultCut | 'all'
          mediaType?: MediaCut | 'all'
          aiInvolvement?: AiInvolvement
          numberOfItems?: string
        }
      }) => ({
        userId: route.query.userId,
        currentId: route.query.currentId,
        customerAccountNumber: route.query.customerAccountNumber,
        monthFrom: route.query.monthFrom,
        monthTo: route.query.monthTo,
        resultType: route.query.resultType,
        mediaType: route.query.mediaType,
        aiInvolvement: route.query.aiInvolvement,
        numberOfItems: route.query.numberOfItems,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/order-ticket-metrics',
      name: 'orderTicketMetrics',
      component: () => import('@/views/admin/OrderTicketMetricsView.vue'),
      props: (route: { query: { month?: string; userId?: string } }) => ({
        month: route.query.month,
        userId: route.query.userId,
      }),
      meta: { requiredAuth: true, adminPage: true, access: 'allBranches' },
    },
    {
      path: '/admin/salesrep-dashboard',
      name: 'salesrepDashboard',
      component: () => import('@/views/admin/SalesrepDashboardView.vue'),
      props: (route: { query: { salesrep_number?: string } }) => ({
        salesrepNumber: route.query.salesrep_number,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/maguchi-insights',
      name: 'maguchiInsights',
      component: () => import('@/views/admin/MaguchiInsightsView.vue'),
      props: (route: { query: { salesrep_number?: string } }) => ({
        salesrepNumber: route.query.salesrep_number,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/customer-insights',
      name: 'customerInsights',
      component: () => import('@/views/admin/CustomerInsightsView.vue'),
      props: (route: { query: { salesrep_number?: string } }) => ({
        salesrepNumber: route.query.salesrep_number,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/customer-ar',
      name: 'customerAr',
      component: () => import('@/views/admin/CustomerArView.vue'),
      props: (route: { query: { salesrep_number?: string } }) => ({
        salesrepNumber: route.query.salesrep_number,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/ar-activities',
      name: 'arActivities',
      component: () => import('@/views/admin/ArActivitiesView.vue'),
      props: (route: { query: { branchOrgId?: string; salesrep_number?: string } }) => ({
        branchOrgId: route.query.branchOrgId,
        salesrepNumber: route.query.salesrep_number,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/account-management',
      name: 'accountManagement',
      component: () => import('@/views/admin/AccountManagementView.vue'),
      props: (route: { query: { salesrep_number?: string } }) => ({
        salesrepNumber: route.query.salesrep_number,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/branch-dashboard',
      name: 'branchDashboard',
      component: () => import('@/views/admin/BranchDashboardView.vue'),
      meta: { requiredAuth: true, adminPage: true, access: 'allBranches' },
    },
    {
      path: '/admin/manager-dashboard',
      name: 'managerDashboard',
      component: () => import('@/views/admin/ManagerDashboardView.vue'),
      props: (route: { query: Record<string, unknown> }) => parseSalesrepActivityQuery(route.query),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/bbs-reader-groups',
      name: 'bbsReaderGroups',
      component: () => import('@/views/admin/BbsReaderGroupListView.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/iris-settings',
      name: 'irisSettings',
      component: () => import('@/views/admin/MaguchiAgentSettingsView.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/sales-profit-analysis',
      name: 'salesProfitAnalysis',
      component: () => import('@/views/admin/SalesProfitAnalysisView.vue'),
      props: (route: { query: Record<string, unknown> }) => parseSalesProfitQuery(route.query),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/bbd',
      name: 'bbd',
      component: () => import('@/views/admin/BbdView.vue'),
      props: (route: { query: { branchOrgId?: string } }) => ({
        branchOrgId: route.query.branchOrgId,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/bbd/uncounted-items',
      name: 'bbdUncountedItems',
      component: () => import('@/views/admin/BbdUncountedItemView.vue'),
      props: (route: { query: { branchOrgId?: string } }) => ({
        branchOrgId: route.query.branchOrgId,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/bbd/notification-groups',
      name: 'bbdNotificationGroups',
      component: () => import('@/views/admin/BbdNotificationGroupView.vue'),
      props: (route: { query: { branchOrgId?: string } }) => ({
        branchOrgId: route.query.branchOrgId,
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/customer-list',
      name: 'customerList',
      component: () => import('@/views/admin/CustomerList.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/lead-list',
      name: 'leadList',
      component: () => import('@/views/admin/LeadList.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/lead-list/location-check',
      name: 'leadLocationCheck',
      component: () => import('@/views/admin/LeadLocationCheckView.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/lead-list/dashboard',
      name: 'leadDashboard',
      component: () => import('@/views/admin/LeadDashboardView.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    // TODO(temporary): order_history の集計精度が上がったら削除する
    {
      path: '/admin/daily-sales-report-import',
      name: 'dailySalesReportImport',
      component: () => import('@/views/admin/DailySalesReportImportView.vue'),
      meta: { requiredAuth: true, adminPage: true, access: 'allBranches' },
    },
    {
      path: '/admin/salesrep-list',
      name: 'salesrepList',
      component: () => import('@/views/admin/SalesrepList.vue'),
      meta: { requiredAuth: true, adminPage: true, access: 'allBranches' },
    },
    {
      path: '/admin/item-list',
      name: 'itemList',
      component: () => import('@/views/admin/ItemList.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/invalid-item-list',
      name: 'invalidItemList',
      component: () => import('@/views/admin/ItemInvalidList.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/invalid-order-list',
      name: 'invalidOrderList',
      component: () => import('@/views/admin/OrderInvalidList.vue'),
      meta: { requiredAuth: true, adminPage: true, access: 'systemAdmin' },
    },
    {
      path: '/admin/market-products',
      name: 'marketProductList',
      component: () => import('@/views/admin/MarketProductListView.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/order-list',
      name: 'orderList',
      component: () => import('@/views/admin/OrderList.vue'),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/orders/:id',
      name: 'orderDetail',
      component: () => import('@/views/admin/OrderDetail.vue'),
      props: true,
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/inquiry-list',
      name: 'inquiryList',
      component: () => import('@/views/admin/InquiryListView.vue'),
      meta: { requiredAuth: true, adminPage: true, access: 'allBranches' },
    },
    {
      path: '/admin/customers/:id/action',
      name: 'customerAction',
      component: () => import('@/views/admin/CustomerActionView.vue'),
      props: (route: {
        params: { id: string }
        query: { conversationId?: string; tab?: string }
      }) => ({
        customerId: route.params.id,
        conversationId: route.query.conversationId,
        tab: parseCustomerActionTab(route.query.tab),
      }),
      meta: { requiredAuth: true, adminPage: true },
    },
    {
      path: '/admin/users',
      name: 'users',
      component: () => import('@/views/admin/UserList.vue'),
      props: (route: { query: Record<string, unknown> }) => parseUsersQuery(route.query),
      meta: { requiredAuth: true, adminPage: true, access: 'allBranches' },
    },
    {
      path: '/admin/app-status',
      name: 'appStatus',
      component: () => import('@/views/admin/AppStatusView.vue'),
      meta: { requiredAuth: true, adminPage: true, access: 'systemAdmin' },
    },
  ],
})

router.beforeEach(async (to, from) => {
  const authStore = useAuthStore()
  if (!to.meta.requiredAuth) return true

  if (authStore.is_loggedin) {
    return checkAccess()
  }

  // valid cookie持ってるかチェック
  try {
    await authStore.authedHello()
    return checkAccess()
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined
    if (status === 401) {
      Sentry.captureMessage('Auth: authedHello failed, redirecting to login', {
        level: 'warning',
        extra: {
          targetRoute: to.fullPath,
          fromRoute: from.fullPath,
          errorStatus: status,
          errorMessage: err instanceof Error ? err.message : String(err),
        },
      })
      return redirectToLogin('Please log in again')
    }
    Sentry.captureMessage('Auth: authedHello transient failure, keeping session', {
      level: 'warning',
      extra: {
        targetRoute: to.fullPath,
        fromRoute: from.fullPath,
        errorStatus: status,
        errorCode: axios.isAxiosError(err) ? err.code : undefined,
        errorMessage: err instanceof Error ? err.message : String(err),
      },
    })
    return { name: 'reconnecting' as const, query: { redirect: to.fullPath } }
  }

  // 開ける人が限られた画面。ログイン済みなので、ログアウトさせずに管理画面のトップへ戻す
  function checkAccess() {
    if (authStore.can_access(to.meta.access)) return true
    message.error(
      to.meta.access === 'systemAdmin'
        ? 'System admin access required'
        : 'All-branch access required',
    )
    return { path: '/admin/salesrep-dashboard' }
  }

  function redirectToLogin(msg: string) {
    // cookie が残って困るのはユーザーがセッション破棄を期待する場合で、強制ログアウトにはその期待がない。
    // そのため logout API が失敗しても画面上はログアウトさせる
    useUserStore().clearCurrentUser()
    authStore.logout().catch(() => {})
    message.error(msg)
    return { name: 'login' as const, query: { authError: 'true' } }
  }
})

export default router
